package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.exp
import kotlin.math.ln
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin
import kotlin.math.sqrt

private val EventKind.outputLevelScale: Float
    get() = when (this) {
        EventKind.Drip, EventKind.Metal, EventKind.Beep -> 0.5f
        else -> 1f
    }

/** Fixed-storage one-shot voice. Samples are exposed through [outLeft]/[outRight]. */
internal class EventVoice(private val sampleRate: Double) {
    private var kind = EventKind.None
    private var level = 0f
    private var delayLow = 1.0
    private var delayHigh = 1.0
    private val random = SplitMix64(0uL)
    private val dovePhrase = DovePhraseState(random)
    private val material = MaterialEventState(sampleRate)
    private val imperfection = AnalogImperfectionState(sampleRate, 0uL)
    private val primaryOscillator = RecursiveSineOscillator(sampleRate)
    private val secondaryOscillator = RecursiveSineOscillator(sampleRate)
    private val microPulseOscillator = RecursiveSineOscillator(sampleRate)

    private var framesUntilEvent = 1
    private var framesRemaining = 0
    private var totalFrames = 1
    private var oscillatorControlCounter = 0
    private var frequency = 700.0
    private var sweep = 0.0
    private var modulationFrequency = 0.0
    private var microPulseFrequency = 0.0
    private var dripImpactFrame = 0
    private var dripFastDecay = 0.001_8
    private var dripBodyDecay = 0.043
    private var dripUpperDecay = 0.017
    private var dripBurstDecay = 0.004_5
    private var dripTailDecay = 0.035
    private var dripUpperRatio = 1.5
    private var dripClickLevel = 0.5f
    private var dripTailNoiseLevel = 0.09f
    private var dripNoiseFast = 0f
    private var dripNoiseSlow = 0f
    private var eventGain = 1f
    private var panLeftGain = 0.707_106_77f
    private var panRightGain = 0.707_106_77f
    private val dripNoiseFastAlpha = (1.0 - exp(-2.0 * PI * 6_000.0 / sampleRate)).toFloat()
    private val dripNoiseSlowAlpha = (1.0 - exp(-2.0 * PI * 1_200.0 / sampleRate)).toFloat()
    private val visualRelease = exp(-1.0 / (0.085 * sampleRate)).toFloat()

    var outLeft = 0f
        private set
    var outRight = 0f
        private set
    var visualActivity = 0f
        private set
    var visualConvergence = 0f
        private set
    val visualSound: AmbienceVisualSound
        get() = kind.visualSound

    fun configure(
        kind: EventKind,
        level: Float,
        delayLow: Double,
        delayHigh: Double,
        seed: ULong,
        startImmediately: Boolean = false,
    ) {
        this.kind = kind
        this.level = level * kind.outputLevelScale
        this.delayLow = delayLow
        this.delayHigh = delayHigh
        random.reset(seed)
        imperfection.reset(seed xor 0x8CB9_2BA7_2F3D_8DD7uL)
        framesUntilEvent = if (startImmediately) 1 else max((delayLow * sampleRate * 0.35).toInt(), 1)
        framesRemaining = 0
        totalFrames = 1
        oscillatorControlCounter = 0
        primaryOscillator.reset(0.0, 0.0)
        secondaryOscillator.reset(0.0, 0.0)
        microPulseOscillator.reset(0.0, 0.0)
        frequency = 700.0
        sweep = 0.0
        modulationFrequency = 0.0
        microPulseFrequency = 0.0
        dovePhrase.reset()
        dripImpactFrame = 0
        dripFastDecay = 0.001_8
        dripBodyDecay = 0.043
        dripUpperDecay = 0.017
        dripBurstDecay = 0.004_5
        dripTailDecay = 0.035
        dripUpperRatio = 1.5
        dripClickLevel = 0.5f
        dripTailNoiseLevel = 0.09f
        dripNoiseFast = 0f
        dripNoiseSlow = 0f
        eventGain = 1f
        material.reset()
        panLeftGain = 0.707_106_77f
        panRightGain = 0.707_106_77f
        outLeft = 0f
        outRight = 0f
        visualActivity = 0f
        visualConvergence = 0f
    }

    fun nextSample(noise: Float) {
        outLeft = 0f
        outRight = 0f
        if (kind == EventKind.None) {
            visualActivity = 0f
            visualConvergence = 0f
            return
        }

        if (framesRemaining <= 0) {
            framesUntilEvent -= 1
            if (framesUntilEvent > 0) {
                visualActivity *= visualRelease
                visualConvergence *= visualRelease
                return
            }
            trigger()
        }

        val elapsedFrames = totalFrames - framesRemaining
        val progress = elapsedFrames.toDouble() / max(totalFrames, 1).toDouble()
        imperfection.nextFrame()
        val higurashiSyllable = if (kind == EventKind.Higurashi) {
            max(0f, secondaryOscillator.sineValue)
        } else {
            0f
        }
        val doveFlutterDepth = if (progress < 0.35) 0.002_0 else 0.000_5
        val doveFlutter = if (kind == EventKind.Dove) {
            1.0 + secondaryOscillator.sineValue * doveFlutterDepth
        } else {
            1.0
        }
        val pitchDrift = if (kind == EventKind.Dove) {
            1.0 + (imperfection.pitchMultiplier - 1.0) * 0.20
        } else {
            imperfection.pitchMultiplier
        }
        val doveNotePitch = if (kind == EventKind.Dove) {
            dovePhrase.notePitchMultiplier(progress * 1.70)
        } else {
            1.0
        }
        val frequencyNow = limitedFrequency(
            max(
                (frequency + sweep * progress) * pitchMultiplier(progress, higurashiSyllable) *
                    doveFlutter * pitchDrift * doveNotePitch,
                18.0,
            ),
        )
        val secondaryFrequency = when (kind) {
            EventKind.Drip -> frequencyNow * dripUpperRatio
            EventKind.Higurashi -> modulationFrequency
            EventKind.Dove -> 18.0
            EventKind.WindChime, EventKind.BuoyBell -> frequencyNow * 2.71
            EventKind.Owl -> frequencyNow * 2.0
            EventKind.Cricket -> frequencyNow * 1.01
            EventKind.Bubble -> frequencyNow * 1.87
            else -> frequencyNow * 1.507
        }
        // Preserve common wow while adding a smaller independent harmonic-ratio drift.
        val imperfectSecondaryFrequency = min(
            secondaryFrequency * imperfection.secondaryPitchMultiplier,
            sampleRate * 0.45,
        )
        if (oscillatorControlCounter == 0) {
            primaryOscillator.setFrequency(frequencyNow)
            secondaryOscillator.setFrequency(imperfectSecondaryFrequency)
            if (kind == EventKind.Higurashi) {
                microPulseOscillator.setFrequency(microPulseFrequency)
            }
        }
        val primarySample = primaryOscillator.next()
        val secondarySample = secondaryOscillator.next()
        val microPulseSample = if (kind == EventKind.Higurashi) {
            microPulseOscillator.next()
        } else {
            0f
        }
        oscillatorControlCounter = (oscillatorControlCounter + 1) and
            (OSCILLATOR_CONTROL_INTERVAL_FRAMES - 1)

        val envelope = envelope(progress)
        val dripPostSeconds = (elapsedFrames - dripImpactFrame).toDouble() / sampleRate
        val dripActivity = if (dripPostSeconds >= 0.0) {
            exp(-dripPostSeconds / 0.045).toFloat()
        } else {
            0f
        }
        val visualEnvelope = when (kind) {
            EventKind.Drip -> dripActivity * eventGain
            EventKind.Higurashi -> envelope * (0.18f + higurashiSyllable * 0.82f)
            else -> envelope
        }
        visualActivity = max(visualEnvelope, visualActivity * visualRelease)
        visualConvergence = if (kind == EventKind.WaveSlap) {
            if (progress < 0.16) {
                (progress / 0.16).toFloat()
            } else {
                max(1.0 - (progress - 0.16) / 0.84, 0.0).toFloat()
            }
        } else {
            visualEnvelope
        }

        val raw = when (kind) {
            EventKind.Drip -> nextDrip(noise, elapsedFrames, dripPostSeconds)
            EventKind.WindChime,
            EventKind.Owl,
            EventKind.Cricket,
            EventKind.Bubble,
            EventKind.FootstepGravel,
            EventKind.FootstepSnow,
            EventKind.BranchSnap,
            EventKind.ClockTick,
            EventKind.BuoyBell,
            EventKind.LeafDrip,
            EventKind.BicyclePass,
            -> material.sample(kind, noise, primarySample, secondarySample, elapsedFrames.toDouble() / sampleRate)
            EventKind.Higurashi -> {
                val fundamental = primarySample
                val second = 2f * fundamental * primaryOscillator.cosineValue
                val carrier = fundamental + second * 0.24f
                val microPulse = 0.52f + 0.48f * max(0f, microPulseSample)
                val syllablePulse = 0.12f + 0.88f * higurashiSyllable
                (carrier * 0.68f + noise * 0.065f) * microPulse * syllablePulse
            }
            EventKind.Dove -> {
                val fundamental = primarySample
                val second = 2f * fundamental * primaryOscillator.cosineValue
                fundamental + second * if (progress < 0.48) 0.001f else 0.006f
            }
            EventKind.Spark, EventKind.Relay, EventKind.TrainJoint, EventKind.Rock -> {
                val oscillator = primarySample + secondarySample * 0.28f
                noise * 0.72f + oscillator * 0.28f
            }
            EventKind.Thunder, EventKind.Radio, EventKind.WaveSlap -> {
                val oscillator = primarySample + secondarySample * 0.28f
                noise * 0.46f + oscillator * 0.54f
            }
            else -> primarySample + secondarySample * 0.28f
        }

        framesRemaining -= 1
        if (framesRemaining <= 0) scheduleNext()
        val gain = envelope * level * eventGain
        val tonalSample = raw * imperfection.amplitudeMultiplier
        val noiseMix = analogNoiseMix()
        outLeft = (tonalSample + imperfection.noiseLeft * noiseMix) * gain * panLeftGain
        outRight = (tonalSample + imperfection.noiseRight * noiseMix) * gain * panRightGain
    }

    private fun nextDrip(noise: Float, elapsedFrames: Int, dripPostSeconds: Double): Float {
        dripNoiseFast += dripNoiseFastAlpha * (noise - dripNoiseFast)
        dripNoiseSlow += dripNoiseSlowAlpha * (noise - dripNoiseSlow)
        val bandNoise = dripNoiseFast - dripNoiseSlow
        if (dripPostSeconds < 0.0) {
            val lead = max(elapsedFrames.toFloat() / max(dripImpactFrame, 1).toFloat(), 0f)
            return bandNoise * 0.025f * lead * lead * lead
        }
        val bodyEnvelope = 0.84 * exp(-dripPostSeconds / dripFastDecay) +
            0.16 * exp(-dripPostSeconds / dripBodyDecay)
        val body = primaryOscillator.sineValue * bodyEnvelope.toFloat()
        val upper = secondaryOscillator.sineValue * 0.18f *
            exp(-dripPostSeconds / dripUpperDecay).toFloat()
        val burst = bandNoise * dripClickLevel * exp(-dripPostSeconds / dripBurstDecay).toFloat()
        val tail = bandNoise * dripTailNoiseLevel * exp(-dripPostSeconds / dripTailDecay).toFloat()
        return body + upper + burst + tail
    }

    private fun trigger() {
        var durationLow = 0.1
        var durationHigh = 0.2
        var baseLow = 100.0
        var baseHigh = 200.0
        var sweepLow = 0.0
        var sweepHigh = 0.0
        when (kind) {
            EventKind.None -> return
            EventKind.Bird -> { durationLow = 0.14; durationHigh = 0.28; baseLow = 1_760.0; baseHigh = 2_600.0; sweepLow = 120.0; sweepHigh = 360.0 }
            EventKind.InsectCall -> { durationLow = 0.42; durationHigh = 0.66; baseLow = 530.0; baseHigh = 590.0; sweepLow = -115.0; sweepHigh = -65.0 }
            EventKind.Drip -> { durationLow = 0.27; durationHigh = 0.34; baseLow = 2_260.0; baseHigh = 2_550.0; sweepLow = -30.0; sweepHigh = 30.0 }
            EventKind.Bell -> { durationLow = 2.2; durationHigh = 4.1; baseLow = 310.0; baseHigh = 520.0; sweepLow = -5.0; sweepHigh = 5.0 }
            EventKind.Metal -> { durationLow = 0.45; durationHigh = 2.8; baseLow = 410.0; baseHigh = 1_100.0; sweepLow = -35.0; sweepHigh = 30.0 }
            EventKind.Creak -> { durationLow = 1.0; durationHigh = 3.8; baseLow = 72.0; baseHigh = 230.0; sweepLow = -50.0; sweepHigh = 90.0 }
            EventKind.Spark -> { durationLow = 0.012; durationHigh = 0.075; baseLow = 1_300.0; baseHigh = 4_500.0; sweepLow = -400.0; sweepHigh = 300.0 }
            EventKind.Relay -> { durationLow = 0.025; durationHigh = 0.11; baseLow = 430.0; baseHigh = 1_100.0; sweepLow = -120.0; sweepHigh = 80.0 }
            EventKind.Frog -> { durationLow = 0.32; durationHigh = 0.9; baseLow = 145.0; baseHigh = 265.0; sweepLow = -35.0; sweepHigh = 55.0 }
            EventKind.Thunder -> { durationLow = 3.1; durationHigh = 7.5; baseLow = 27.0; baseHigh = 66.0; sweepLow = -14.0; sweepHigh = 7.0 }
            EventKind.Radio -> { durationLow = 0.7; durationHigh = 3.4; baseLow = 1_100.0; baseHigh = 2_900.0; sweepLow = -800.0; sweepHigh = 800.0 }
            EventKind.TrainJoint -> { durationLow = 0.045; durationHigh = 0.12; baseLow = 75.0; baseHigh = 170.0; sweepLow = -25.0; sweepHigh = 20.0 }
            EventKind.Beep -> { durationLow = 0.12; durationHigh = 0.35; baseLow = 760.0; baseHigh = 1_240.0; sweepLow = -8.0; sweepHigh = 8.0 }
            EventKind.Rock -> { durationLow = 0.7; durationHigh = 2.2; baseLow = 34.0; baseHigh = 115.0; sweepLow = -28.0; sweepHigh = 40.0 }
            EventKind.WaveSlap -> { durationLow = 0.7; durationHigh = 2.1; baseLow = 95.0; baseHigh = 330.0; sweepLow = -80.0; sweepHigh = 40.0 }
            EventKind.Higurashi -> { durationLow = 2.4; durationHigh = 5.6; baseLow = 4_500.0; baseHigh = 6_200.0; sweepLow = -140.0; sweepHigh = 80.0 }
            EventKind.Dove -> { durationLow = 1.35; durationHigh = 2.05; baseLow = 443.0; baseHigh = 443.0; sweepLow = 0.0; sweepHigh = 0.0 }
            EventKind.WindChime -> { durationLow = 3.100; durationHigh = 4.800; baseLow = 1400.0; baseHigh = 2200.0; sweepLow = -6.0; sweepHigh = 6.0 }
            EventKind.Owl -> { durationLow = 2.200; durationHigh = 2.800; baseLow = 330.0; baseHigh = 410.0; sweepLow = -25.0; sweepHigh = 15.0 }
            EventKind.Cricket -> { durationLow = 1.100; durationHigh = 1.600; baseLow = 3200.0; baseHigh = 4100.0; sweepLow = 10.0; sweepHigh = 60.0 }
            EventKind.Bubble -> { durationLow = 0.250; durationHigh = 0.500; baseLow = 280.0; baseHigh = 530.0; sweepLow = 500.0; sweepHigh = 1100.0 }
            EventKind.FootstepGravel -> { durationLow = 1.100; durationHigh = 1.500; baseLow = 100.0; baseHigh = 180.0; sweepLow = -20.0; sweepHigh = 20.0 }
            EventKind.FootstepSnow -> { durationLow = 1.300; durationHigh = 1.800; baseLow = 75.0; baseHigh = 130.0; sweepLow = -15.0; sweepHigh = 10.0 }
            EventKind.BranchSnap -> { durationLow = 0.250; durationHigh = 0.450; baseLow = 640.0; baseHigh = 1250.0; sweepLow = -180.0; sweepHigh = -50.0 }
            EventKind.ClockTick -> { durationLow = 0.180; durationHigh = 0.250; baseLow = 1100.0; baseHigh = 1500.0; sweepLow = -30.0; sweepHigh = 30.0 }
            EventKind.LeafDrip -> { durationLow = 1.0; durationHigh = 1.4; baseLow = 650.0; baseHigh = 950.0; sweepLow = -160.0; sweepHigh = -80.0 }
            EventKind.BicyclePass -> { durationLow = 4.5; durationHigh = 6.0; baseLow = 65.0; baseHigh = 100.0; sweepLow = -15.0; sweepHigh = -5.0 }
            EventKind.BuoyBell -> { durationLow = 4.500; durationHigh = 6.000; baseLow = 180.0; baseHigh = 290.0; sweepLow = -3.0; sweepHigh = 3.0 }
        }

        material.reset()
        val durationDraw = random.nextUnitDouble()
        val durationSeconds = durationLow + (durationHigh - durationLow) * durationDraw
        totalFrames = max((durationSeconds * sampleRate).toInt(), 1)
        framesRemaining = totalFrames
        val frequencyDraw = random.nextUnitDouble()
        val weakDrip = kind == EventKind.Drip && durationDraw < 0.43
        frequency = when {
            weakDrip -> 2_460.0 + 90.0 * frequencyDraw
            kind == EventKind.Drip -> 2_260.0 + 140.0 * frequencyDraw
            else -> baseLow + (baseHigh - baseLow) * frequencyDraw
        }
        sweep = randomDouble(sweepLow, sweepHigh)
        if (kind == EventKind.Drip) {
            val leadSeconds = if (weakDrip) randomDouble(0.009, 0.014) else randomDouble(0.005, 0.007)
            dripImpactFrame = max((leadSeconds * sampleRate).toInt(), 1)
            dripFastDecay = randomDouble(0.001_3, 0.002_4)
            dripBodyDecay = randomDouble(0.038, 0.048)
            dripUpperDecay = randomDouble(0.012, 0.022)
            dripBurstDecay = randomDouble(0.003, 0.006)
            dripTailDecay = randomDouble(0.025, 0.045)
            dripUpperRatio = randomDouble(1.42, 1.58)
            dripClickLevel = randomFloat(0.25f, 0.45f)
            dripTailNoiseLevel = randomFloat(0.06f, 0.12f)
            dripNoiseFast = 0f
            dripNoiseSlow = 0f
            eventGain = if (weakDrip) {
                (0.20 + durationDraw / 0.43 * 0.03).toFloat()
            } else {
                (0.67 + (durationDraw - 0.43) / 0.57 * 0.33).toFloat()
            }
        } else {
            eventGain = 1f
        }
        modulationFrequency = if (kind == EventKind.Higurashi) randomDouble(8.0, 14.0) else 0.0
        if (kind == EventKind.Dove) {
            dovePhrase.randomize()
        } else {
            dovePhrase.reset()
        }
        if (kind == EventKind.Higurashi) {
            microPulseFrequency = randomDouble(180.0, 350.0)
            microPulseOscillator.reset(
                random.nextUnitDouble() * PI * 2.0,
                microPulseFrequency,
            )
        } else {
            microPulseFrequency = 0.0
            microPulseOscillator.reset(0.0, 0.0)
        }
        val pan = randomFloat(-0.38f, 0.38f)
        panLeftGain = sqrt((1f - pan) * 0.5f)
        panRightGain = sqrt((1f + pan) * 0.5f)
        primaryOscillator.reset(random.nextUnitDouble() * PI * 2.0, frequency)
        secondaryOscillator.reset(random.nextUnitDouble() * PI * 2.0, frequency * 1.507)
        oscillatorControlCounter = 0
    }

    private fun envelope(progress: Double): Float = when (kind) {
        EventKind.WindChime,
        EventKind.Owl,
        EventKind.Cricket,
        EventKind.Bubble,
        EventKind.FootstepGravel,
        EventKind.FootstepSnow,
        EventKind.BranchSnap,
        EventKind.ClockTick,
        EventKind.BuoyBell,
        EventKind.LeafDrip,
        EventKind.BicyclePass,
        -> material.envelope(kind, progress, totalFrames)
        EventKind.Higurashi -> {
            val attackDuration = 0.12
            val releaseDuration = 0.22
            when {
                progress < attackDuration -> sin(PI * 0.5 * progress / attackDuration).toFloat()
                progress > 1.0 - releaseDuration -> sin(PI * 0.5 * (1.0 - progress) / releaseDuration).toFloat()
                else -> 1f
            }
        }
        EventKind.Dove -> dovePhrase.envelope(progress * 1.70).toFloat()
        EventKind.Drip -> 1f
        EventKind.Thunder, EventKind.WaveSlap, EventKind.Radio -> {
            val attack = min(progress / 0.16, 1.0)
            val decay = max(1.0 - progress, 0.0)
            (sin(PI * 0.5 * attack) * decay * decay).toFloat()
        }
        EventKind.Spark, EventKind.Relay, EventKind.TrainJoint, EventKind.Rock ->
            (max(1.0 - progress, 0.0) * exp(-5.0 * progress)).toFloat()
        else -> {
            val sine = sin(PI * progress).toFloat()
            sine * sine
        }
    }

    private fun pitchMultiplier(progress: Double, higurashiSyllable: Float): Double = when (kind) {
        EventKind.Dove -> dovePhrase.pitch(progress * 1.70)
        EventKind.Higurashi -> 1.0 - higurashiSyllable.toDouble() * 0.035
        else -> 1.0
    }

    private fun limitedFrequency(value: Double): Double {
        val harmonicCount = when (kind) {
            EventKind.Dove -> 3.0
            EventKind.Higurashi -> 2.0
            EventKind.WindChime, EventKind.BuoyBell -> 2.71
            EventKind.Owl -> 2.0
            EventKind.Bubble -> 1.87
            else -> 1.507
        }
        return min(value, sampleRate * 0.45 / harmonicCount)
    }

    private fun scheduleNext() {
        val unit = max(random.nextUnitDouble(), 0.000_001)
        val exponential = -ln(unit) / 1.35
        val normalized = min(exponential, 1.0)
        val seconds = delayLow + (delayHigh - delayLow) * normalized
        framesUntilEvent = max((seconds * sampleRate).toInt(), 1)
    }

    private fun randomDouble(low: Double, high: Double): Double =
        low + (high - low) * random.nextUnitDouble()

    private fun randomFloat(low: Float, high: Float): Float =
        low + (high - low) * random.nextUnitFloat()

    private fun analogNoiseMix(): Float = when (kind) {
        EventKind.Bird,
        EventKind.InsectCall,
        EventKind.Bell,
        EventKind.Metal,
        EventKind.Creak,
        EventKind.Frog,
        EventKind.Beep,
        EventKind.Dove,
        -> AnalogImperfectionState.NOISE_MIX
        EventKind.Drip, EventKind.Higurashi -> AnalogImperfectionState.NOISE_MIX * 0.45f
        else -> AnalogImperfectionState.NOISE_MIX * 0.20f
    }

    private companion object {
        const val OSCILLATOR_CONTROL_INTERVAL_FRAMES = 16
    }
}
